import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { availabilityNote } from "../lib/productAvailability";
import { Modal } from "../pages/admin/AdminRestaurants";
import BigToggle from "./BigToggle";

/**
 * Merchant Inventory "Available" switch for one product. Switching off asks for how long — until the
 * merchant switches it back on, or just for today (the original auto-resume) — and never edits the product itself.
 */
export default function ProductAvailabilityToggle({ product, onChanged, testid }) {
    const [asking, setAsking] = useState(false);
    const [busy, setBusy] = useState(false);
    // Same test the server's toggle uses: only ACTIVE is on (OUT_OF_STOCK shows off and switching it on activates it).
    const on = product.status === "ACTIVE";
    const note = availabilityNote(product);

    const save = async (active, until) => {
        setBusy(true);
        try {
            await api.patch(`/merchant/inventory/${product.id}/toggle?active=${active}${until ? `&until=${until}` : ""}`);
            toast.success(active ? `${product.name} is available again` : `${product.name} turned off`);
            setAsking(false);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="inline-flex flex-col items-start gap-1">
            <BigToggle testid={testid} on={on} disabled={busy} onClick={() => (on ? setAsking(true) : save(true))} />
            {note && <span className="text-[10px] text-muted-foreground" data-testid={`${testid}-note`}>{note}</span>}
            {asking && (
                <Modal title={`Turn off ${product.name}?`} onClose={() => setAsking(false)}>
                    <p className="text-sm text-muted-foreground">
                        Customers can&apos;t see or order it while it&apos;s off. Its details, prices and stock stay as they are.
                    </p>
                    <div className="mt-4 grid gap-2">
                        <button data-testid={`${testid}-manual`} disabled={busy} onClick={() => save(false, "MANUAL")}
                            className="w-full text-left rounded-xl border border-border px-4 py-3 hover:bg-muted disabled:opacity-50">
                            <p className="text-sm font-bold">Until I turn it back on</p>
                            <p className="text-[11px] text-muted-foreground">Stays off until you switch it on again</p>
                        </button>
                        <button data-testid={`${testid}-today`} disabled={busy} onClick={() => save(false, "TOMORROW")}
                            className="w-full text-left rounded-xl border border-border px-4 py-3 hover:bg-muted disabled:opacity-50">
                            <p className="text-sm font-bold">For today</p>
                            <p className="text-[11px] text-muted-foreground">Back on automatically tomorrow at 9:00 AM</p>
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
